/**
 * useProducts Hook
 *
 * Provides product functionality to React components.
 * Integrates Orval-generated mutation hooks for API operations.
 * This is the primary interface for accessing products in the presentation layer.
 */

import { useContext } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ProductContext } from "../contexts/ProductContext";
import {
  useExternalItemsControllerCreateRequest,
  useExternalItemsControllerUpdateSettings,
  getExternalItemsControllerGetItemsQueryKey,
  getExternalCategoriesControllerGetCategoryTreeQueryKey,
  type ExternalItemsControllerGetItems200,
  type UpdateSupplierItemSettingsDto,
  type SupplierCategoryItemDto,
} from "@semo/api-contracts";

export const useProducts = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error("useProducts must be used within ItemsProvider");
  }

  const queryClient = useQueryClient();

  // Orval mutation hooks
  // ✅ REPLACED: Admin Create -> Vendor Create Request
  const createProduct = useExternalItemsControllerCreateRequest();

  // ✅ REPLACED: Admin Update -> Vendor Update Settings (Price/Status)
  const updateProduct = useExternalItemsControllerUpdateSettings();

  // ✅ NOTE: Vendors cannot delete products directly. Removed Admin Delete hook.
  const deleteProduct = {
    mutateAsync: async () => {
      console.warn("Delete not supported for vendors");
    },
  } as any;

  const updateSettings = useExternalItemsControllerUpdateSettings({
    mutation: {
      onSuccess: (
        _response,
        variables: { id: string; data: UpdateSupplierItemSettingsDto },
      ) => {
        // Use the generated query key factory for type safety and consistency
        const queryKey = getExternalItemsControllerGetItemsQueryKey();

        // 1. Immediate Cache Update (Optimistic UI)
        // Updates all cached lists (filtered by category, etc.) that contain this product
        queryClient.setQueriesData<ExternalItemsControllerGetItems200>(
          { queryKey },
          (oldData: ExternalItemsControllerGetItems200 | undefined) => {
            if (!oldData?.data?.items) {
              return oldData;
            }

            return {
              ...oldData,
              data: {
                ...oldData.data,
                items: oldData.data.items.map(
                  (item: SupplierCategoryItemDto) => {
                    if (item.id === variables.id) {
                      // Merge existing item with new data (price, isSelling)
                      return { ...item, ...variables.data };
                    }
                    return item;
                  },
                ),
              },
            };
          },
        );

        // 2. Invalidate to ensure eventual consistency
        queryClient.invalidateQueries({ queryKey });
        queryClient.invalidateQueries({
          queryKey: getExternalCategoriesControllerGetCategoryTreeQueryKey(),
        });
      },
    },
  });

  return {
    ...context,
    createProduct,
    updateProduct,
    deleteProduct,
    updateSettings,
  };
};
